// orchestrator.fetcher.ts

import {
  OperationStateTypes,
  OperationStatusResponse,
  OrchestratorResourcesBody,
  ResourceReservationParams,
  SwitchClinicalSpecialistBody,
  SwitchConsoleBody,
} from '@/api/apis/orchestrator';
import { api } from '@/api';
import { Uuid } from '@/api/types';

const checkOperationStatus = async (job: OperationStatusResponse, stateType: OperationStateTypes) => {
  if (!job.correlationId) {
    return job;
  }

  return new Promise((resolve, reject) => {
    const intervalId = setInterval(async () => {
      const newJobStatus = await api.orchestrator.checkOperationStatus(job.correlationId!, stateType);

      if (newJobStatus.currentState === 'Final') {
        resolve(newJobStatus);
        clearInterval(intervalId);
      } else if (newJobStatus.faultedMessage) {
        reject(newJobStatus);
        clearInterval(intervalId);
      }
    }, 1000);
  });
};

export const reserveCaseResource = async ({
  caseId,
  ...data
}: Partial<OrchestratorResourcesBody> & ResourceReservationParams) => {
  const jobStatus = await api.orchestrator.reserveResources(caseId, data);

  return checkOperationStatus(jobStatus, OperationStateTypes.ReserveResource);
};

export const removeReservedCaseResource = async ({
  caseId,
  ...data
}: Partial<OrchestratorResourcesBody> & ResourceReservationParams) => {
  const jobStatus = await api.orchestrator.removeReservedResources(caseId, data);

  return checkOperationStatus(jobStatus, OperationStateTypes.RemoveReservations);
};

export const bookCaseResource = async ({ caseId }: ResourceReservationParams) => {
  const jobStatus = await api.orchestrator.bookResources(caseId);

  return checkOperationStatus(jobStatus, OperationStateTypes.ResourcesBookingSend);
};

export const bookSpecificCaseResource = async ({
  caseId,
  ...data
}: Partial<OrchestratorResourcesBody> & ResourceReservationParams) => {
  const jobStatus = await api.orchestrator.bookSpecificResources(caseId, data);

  return checkOperationStatus(jobStatus, OperationStateTypes.ResourcesBookingSend);
};

export const switchOcsConsole = async ({
  caseId,
  previousOcsConsoleId,
  ocsConsoleId,
}: SwitchConsoleBody & ResourceReservationParams) => {
  const jobStatus = await api.orchestrator.switchConsole(caseId, previousOcsConsoleId, ocsConsoleId);

  return checkOperationStatus(jobStatus, OperationStateTypes.SwitchOcsConsole);
};

export const switchClinicalSpecialist = async ({
  caseId,
  previousResourceId,
  resourceId,
}: SwitchClinicalSpecialistBody & ResourceReservationParams) => {
  const jobStatus = await api.orchestrator.switchClinicalSpecialist(caseId, previousResourceId, resourceId);

  return checkOperationStatus(jobStatus, OperationStateTypes.SwitchResource);
};

export const removeHospitalContact = async ({ id }: { id: string }) => {
  const jobStatus = await api.orchestrator.removeHospitalContactWithCorrelation(id);

  return checkOperationStatus(jobStatus, OperationStateTypes.DeleteHospitalContact);
};

export const removeOpoContact = async ({ id }: { id: string }): Promise<void> => {
  await api.orchestrator.removeOpoContact(id);
};

export const removeCustomer = async (id: string) => {
  const jobStatus = await api.orchestrator.removeCustomerById(id);

  return checkOperationStatus(jobStatus, OperationStateTypes.DeleteCustomer);
};

export const removeTxCenter = async (id: Uuid) => {
  const jobStatus = await api.orchestrator.removeTXCenterById(id);

  return checkOperationStatus(jobStatus, OperationStateTypes.DeleteTxCenter);
};

export const removeHospital = async (id: Uuid) => {
  const jobStatus = await api.orchestrator.removeHospitalById(id);

  return checkOperationStatus(jobStatus, OperationStateTypes.DeleteHospital);
};

export const removeOpo = async (id: Uuid): Promise<void> => {
  await api.orchestrator.removeOpoById(id);
};

export const removeHub = async (id: Uuid) => {
  const jobStatus = await api.orchestrator.removeHubById(id);

  return checkOperationStatus(jobStatus, OperationStateTypes.DeleteHub);
};

export const transferResources = async (fromCaseId: Uuid, toCaseId: Uuid) => {
  const jobStatus = await api.orchestrator.transferResources(fromCaseId, toCaseId);

  return checkOperationStatus(jobStatus, OperationStateTypes.TransferResources);
};
